/**
 * ListenerBinder — TF-210
 *
 * Binds step-level event listeners returned by the evaluate-step API.
 * Listeners are cleaned up on step dismiss/navigation.
 *
 * MVP Listeners:
 *   - click — CTA button click tracking
 *   - scroll — Scroll depth tracking (e.g., 50%, 75%)
 *   - timer — Time-based trigger (e.g., after 5 seconds)
 *   - page_view — Page load tracking
 */

import { useEffect, useRef } from 'react';
import { useOptionalFunnelContext } from '../funnel-provider';
import { createEventEnvelope, isFunnelUiEventsEnabled } from '../events';
import { normalizeEventName } from '@trafilea/tss-lib-funnel-schema-ts';
import type { StepListener } from '../types';
import { hasListenerFired, markListenerFired, resolveListenerLifecycle } from './listener-lifecycle';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * The shared StepListener.config is `Record<string, unknown>` (H9 — reactions
 * may carry nested shapes). ListenerBinder only reads a handful of scalar keys,
 * so we coerce them defensively at the read boundary rather than trusting the
 * static type.
 */
type ListenerConfig = StepListener['config'];

interface ListenerBinderProps {
  listeners: StepListener[];
  /** Lifecycle mode from the funnel — controls how often listeners fire. */
  triggerLifecycle?: string;
  /** Unique step ID to track listener state. */
  stepId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readString(config: ListenerConfig, key: string): string | undefined {
  const value = config[key];
  return typeof value === 'string' ? value : undefined;
}

function readNumber(config: ListenerConfig, key: string): number | undefined {
  const value = config[key];
  return typeof value === 'number' ? value : undefined;
}

function fireCallback(
  config: ListenerConfig,
  ctx: ReturnType<typeof useOptionalFunnelContext>,
): void {
  const pixelUrl = readString(config, 'pixel_url');
  if (pixelUrl) {
    const img = new Image();
    img.src = pixelUrl;
  }
  const eventName = readString(config, 'event_name');
  if (eventName) {
    // D-G: the bus carries plain behavioral names so plain-name step listeners
    // match; the ui.* wire prefix is applied only at transport. Compatible
    // prefixed authored names are normalized here (full leg unification: WP-2 H6).
    const name = normalizeEventName(eventName);
    if (isFunnelUiEventsEnabled() && ctx) {
      ctx.eventBus.emit(
        createEventEnvelope(
          name,
          'storefront-funnel',
          { params: { ...config }, context: {} },
          {
            correlationId: ctx.sessionId,
            scope: { tenantId: ctx.config.tenantId },
          },
        ),
      );
    } else {
      window.dispatchEvent(new CustomEvent(eventName, { detail: config }));
    }
  }
}

// ---------------------------------------------------------------------------
// ListenerBinder Component
// ---------------------------------------------------------------------------

export function ListenerBinder({ listeners, triggerLifecycle = 'session', stepId }: ListenerBinderProps) {
  const cleanupRef = useRef<Array<() => void>>([]);
  const funnelCtx = useOptionalFunnelContext();

  useEffect(() => {
    const cleanups: Array<() => void> = [];

    for (const listener of listeners) {
      // A listener-level lifecycle (authored on the card) wins over the
      // step-level default; shared with the bus leg (useStepReactions).
      const lifecycle = resolveListenerLifecycle(listener.config, triggerLifecycle);
      if (hasListenerFired(stepId, listener.id, lifecycle)) continue;

      switch (listener.eventName) {
        case 'click': {
          const handler = () => {
            markListenerFired(stepId, listener.id, lifecycle);
            fireCallback(listener.config, funnelCtx);
          };
          const selector = readString(listener.config, 'selector');
          const target = selector
            ? document.querySelector(selector)
            : document;
          if (target) {
            target.addEventListener('click', handler, { once: true });
            cleanups.push(() => target.removeEventListener('click', handler));
          }
          break;
        }
        case 'scroll':
        case 'scroll_depth': {
          const threshold = readNumber(listener.config, 'scroll_depth') ?? 50;
          const handler = () => {
            const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
            if (scrollableHeight <= 0) return;
            const scrollPercent = (window.scrollY / scrollableHeight) * 100;
            if (scrollPercent >= threshold) {
              markListenerFired(stepId, listener.id, lifecycle);
              fireCallback(listener.config, funnelCtx);
              window.removeEventListener('scroll', handler);
            }
          };
          window.addEventListener('scroll', handler, { passive: true });
          cleanups.push(() => window.removeEventListener('scroll', handler));
          break;
        }
        case 'timer':
        case 'timer_expired': {
          const delay = readNumber(listener.config, 'delay_ms') ?? 5000;
          const timerId = window.setTimeout(() => {
            markListenerFired(stepId, listener.id, lifecycle);
            fireCallback(listener.config, funnelCtx);
          }, delay);
          cleanups.push(() => clearTimeout(timerId));
          break;
        }
        case 'page_view': {
          // TF-AUD-014: Guard against multi-fire on re-renders.
          if (!hasListenerFired(stepId, listener.id, lifecycle)) {
            markListenerFired(stepId, listener.id, lifecycle);
            fireCallback(listener.config, funnelCtx);
          }
          break;
        }
        default:
          // Named behavioral events are owned by useStepReactions, which
          // subscribes to the FunnelEventBus. This binder intentionally owns
          // only the legacy DOM/timer trigger vocabulary above.
          break;
      }
    }

    cleanupRef.current = cleanups;

    // Capture the array in a local variable so the cleanup closure does not
    // read through the mutable ref — prevents stale-ref listener leaks.
    const capturedCleanups = cleanups;
    return () => {
      for (const cleanup of capturedCleanups) {
        cleanup();
      }
      cleanupRef.current = [];
    };
  }, [listeners, stepId, triggerLifecycle]);

  // Renderless component
  return null;
}
